/* eslint-disable react-hooks/exhaustive-deps */
import React, { useReducer, useEffect, useMemo } from 'react'
import { func, string, bool, node } from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { css } from '@emotion/core'
import { useMakeTestId, useGetClassName } from '@divvy-web/hooks.usenameformatter'
import Spinner from '@divvy-web/skylab.spinner'
import { renderWhen } from '@divvy-web/utils'
import Modal from '@divvy-web/skylab.modal'
import useScript from 'react-script-hook'
import { MX_EVENTS, MX_TYPE } from './MXWidgetConstants'

const initialState = {}

export const reducer = (state, { metadata, type }) => {
  const {
    current,
    initial_step: initialStep,
    member_guid: memberGuid,
    name,
    previous,
    trapped,
    user_guid: userGuid
  } = metadata || {}
  if (type === MX_TYPE.DISCONNECT_INITIATED && trapped === true) return { isDisconnectedInitiated: true }
  if (type === MX_TYPE.DISCONNECTED) return { isDisconnecting: true }
  if (type === MX_TYPE.SELECTED_INSTITUTION) return { selectedInstitutionName: name }
  if (type === MX_TYPE.MEMBER_CONNECTED) return { memberGuid, userGuid }
  if (initialStep === MX_EVENTS.SEARCH) return { isSearching: true }
  if (type === MX_TYPE.STEP_CHANGE) {
    return {
      isConnected: previous === MX_EVENTS.CONNECTED,
      isConnecting: current === MX_EVENTS.CONNECTING,
      isSearching: current === MX_EVENTS.SEARCH
    }
  }
  return state
}

const MXWidget = ({
  className,
  isLoadingWidgetUrl,
  isShowing,
  onBankAccountConnected,
  onClose,
  onDisconnect,
  onDisconnectInitiated,
  renderInsideModal,
  searchPageContent,
  setInstitutionName,
  setIsConnecting,
  widgetUrl,
  widgetUrlError
}) => {
  const makeTestId = useMakeTestId('MxWidget')
  const getClassName = useGetClassName('MxWidget', className)
  const [mxScriptLoading, mxScriptError] = useScript({ src: 'https://atrium.mx.com/connect.js' })
  const anchorId = useMemo(() => uuidv4(), [])
  const [{
    isConnected,
    isConnecting,
    isDisconnectedInitiated,
    isDisconnecting,
    isSearching,
    memberGuid,
    selectedInstitutionName,
    userGuid
  }, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (!isDisconnectedInitiated || !onDisconnectInitiated) return
    onDisconnectInitiated()
  }, [isDisconnectedInitiated])

  useEffect(() => {
    if (!isDisconnecting || !onDisconnect) return
    onDisconnect()
  }, [isDisconnecting])

  useEffect(() => {
    if (!selectedInstitutionName || !setInstitutionName) return
    setInstitutionName(selectedInstitutionName)
  }, [selectedInstitutionName])

  useEffect(() => {
    if (!setIsConnecting) return
    setIsConnecting(isConnecting)
  }, [isConnecting])

  useEffect(() => {
    if (!memberGuid || !onBankAccountConnected) return
    onBankAccountConnected(memberGuid, userGuid)
  }, [memberGuid, userGuid])

  useEffect(() => {
    if (!isConnected || !onClose) return
    onClose({ connected: true })
  }, [isConnected])

  const eventListener = (event) => {
    const { data } = event
    if (!data?.mx) return
    const { metadata, type } = data
    dispatch({ metadata, type })
  }

  // Create an instance of MXConnect with settings appropriate for your app.
  // There should only be one instance of MXConnect in your app.
  const loadMX = () => {
    const mxConnect = new window.MXConnect({
      config: {
        ui_message_version: 4
      },
      id: anchorId,
      url: widgetUrl
    })

    !widgetUrlError && !isLoadingWidgetUrl && widgetUrl && mxConnect.load()
  }

  useEffect(() => {
    if (mxScriptLoading || mxScriptError) return

    if (!isLoadingWidgetUrl) {
      loadMX()
      window.addEventListener('message', eventListener)
    }
    return () => {
      window.removeEventListener('message', eventListener)
    }
  }, [isLoadingWidgetUrl, mxScriptLoading, mxScriptError])

  const whenWidgetUrlLoads = renderWhen(isLoadingWidgetUrl, <Spinner centered fullPage dataTestId={makeTestId('')} />)

  const children = (
    <>
      {whenWidgetUrlLoads(<div css={widgetCss} id={anchorId} />)}
      {isSearching && searchPageContent}
    </>
  )

  if (renderInsideModal) {
    return (
      <Modal
        className={getClassName('')}
        css={modalCss}
        dataTestId={makeTestId('')}
        isShowing={isShowing}
        onCloseClick={!isConnecting ? onClose : undefined}
        title=' '
      >
        {children}
      </Modal>
    )
  } else {
    return children
  }
}

const widgetCss = css`
  width: 100%;
  min-width: 320px;
`

const modalCss = css`
  .Modal-main {
    width: 500px;
    min-height: 770px;
  }
`

MXWidget.defaultProps = {
  renderInsideModal: true
}

MXWidget.propTypes = {
  className: string,
  isLoadingWidgetUrl: bool,
  isShowing: bool,
  onBankAccountConnected: func,
  onClose: func,
  onDisconnect: func,
  onDisconnectInitiated: func,
  renderInsideModal: bool,
  searchPageContent: node,
  setInstitutionName: func,
  setIsConnecting: func,
  widgetUrl: string,
  widgetUrlError: string
}

export default MXWidget
